'use client';

import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["size", "secondary", "selected", "selectedColorSet", "className", "children", "iconLeading", "iconTrailing", "variant", "as", "role", "href", "disabled", "imageSrc", "contrastingColor", "borderedColor", "aria-checked", "data-encore-chip-id"];
import React, { useContext } from 'react';
import { BrowserDefaultFocusStyleContext } from "../../contexts/BrowserDefaultFocusStyleContext.mjs";
import { EncoreContext, encoreContextKeyword, getStatus } from "../../contexts/EncoreContext.mjs";
import { KeyboardDetectionContext } from "../../contexts/KeyboardDetectionContext.mjs";
import { ENCORE_DATA_IDS } from "../../dataEncoreIds.mjs";
import { addColorSet, getLegacyControlSize, getNextControlSize, textClass } from "../../styles/index.mjs";
import { ChipGroupContext } from "../ChipGroup/ChipGroupContext.mjs";
import { ChipComponent } from "./Chip.mjs";
import { ChipIcon } from "./Icon.mjs";
import { ChipImage } from "./Image.mjs";
import { Label } from "./Label.mjs";
import { LegacyChipComponent } from "./LegacyChip.mjs";
import { LegacyChipInner } from "./LegacyChipInner.mjs";
import { warn } from "../../logging.mjs";
var chipVariants = ['tinted', 'contrasting', 'bordered'];
function getAriaChecked(selected, role, ariaChecked) {
  if (ariaChecked !== undefined) {
    return ariaChecked;
  }
  if (role === undefined || role === 'checkbox' || role === 'radio') {
    return !!selected;
  }
  return selected;
}

/**
 * Chip has been expanded to a family of components, including:
 *
 * - **[ChipGroup](https://sites.spotify.net/spa/@spotify-internal-encore-web/?path=/docs/components-chipgroup--docs)**: Provides accessibility and layout for groups of Chips, and upgrades them to "Next" Chip.
 * - **[ChipFilter](https://sites.spotify.net/spa/@spotify-internal-encore-web/?path=/docs/components-chipfilter--docs)**: Use for standard filtering use cases. For multi-level chips, use the [old Chip API](https://sites.spotify.net/spa/@spotify-internal-encore-web/?path=/docs/components-chip-multi-level-chip--docs).
 * - **[ChipInput](https://sites.spotify.net/spa/@spotify-internal-encore-web/?path=/docs/components-chipinput--docs)**: Use for input and user-generated use cases.
 * - **Chip (Next Lifecycle)**: Use for all other use cases.
 *
 * Access the "Next" Chip API by wrapping Chips in a ChipGroup. For standalone chips, use Encore Context to configure chip to the next lifecycle.
 *
 * [Storybook](https://encore-web.spotify.net/?path=/docs/components-chip--docs) |
 * [Encore site](https://encore.spotify.net/web/components/chips) |
 * [Github](https://ghe.spotify.net/encore/web/tree/master/src/core/components/Chip) |
 * [Figma](https://www.figma.com/design/bnQXqGkLeiTlbk8JXGDcsX/%E2%9A%99%EF%B8%8F-%E2%80%8A%E2%80%8BEncore-Web-Components?m=auto&node-id=34892-14611&t=09m9nEoe87Yk6rya-1)
 */
export var Chip = /*#__PURE__*/React.forwardRef(function Chip(_ref, ref) {
  var size = _ref.size,
    secondary = _ref.secondary,
    selected = _ref.selected,
    _ref$selectedColorSet = _ref.selectedColorSet,
    selectedColorSet = _ref$selectedColorSet === void 0 ? 'invertedDark' : _ref$selectedColorSet,
    className = _ref.className,
    children = _ref.children,
    iconLeading = _ref.iconLeading,
    iconTrailing = _ref.iconTrailing,
    variant = _ref.variant,
    as = _ref.as,
    role = _ref.role,
    href = _ref.href,
    disabled = _ref.disabled,
    imageSrc = _ref.imageSrc,
    _ref$contrastingColor = _ref.contrastingColor,
    contrastingColor = _ref$contrastingColor === void 0 ? 'brightAccent' : _ref$contrastingColor,
    _ref$borderedColor = _ref.borderedColor,
    borderedColor = _ref$borderedColor === void 0 ? 'base' : _ref$borderedColor,
    ariaChecked = _ref['aria-checked'],
    dataEncoreChipId = _ref['data-encore-chip-id'],
    props = _objectWithoutProperties(_ref, _excluded);
  var _useContext = useContext(KeyboardDetectionContext),
    isUsingKeyboard = _useContext.isUsingKeyboard;
  var _useContext2 = useContext(BrowserDefaultFocusStyleContext),
    useBrowserDefaultFocusStyle = _useContext2.useBrowserDefaultFocusStyle;
  var config = useContext(EncoreContext);
  var status = getStatus(encoreContextKeyword.chip, config);
  var _useContext3 = useContext(ChipGroupContext),
    chipItems = _useContext3.chipItems;
  var handleAddingChipRef = function handleAddingChipRef(node) {
    if (dataEncoreChipId) {
      if (node && chipItems) {
        chipItems.set(dataEncoreChipId, node);
      } else if (chipItems) {
        chipItems["delete"](dataEncoreChipId);
      }
    }
    // Make sure that we are handling the ref passed from the customer
    if (ref) {
      if (typeof ref === 'function') {
        ref(node);
      } else {
        ref.current = node;
      }
    }
  };
  if (status === 'next') {
    var _defaultRole = href ? 'link' : 'button';
    var defaultElement = href ? 'a' : 'button';
    var _chipSize = size ? getNextControlSize(size) : 'small';
    var IconLeading = iconLeading;
    var IconTrailing = iconTrailing;
    var contrastingColorSet = variant === 'contrasting' ? contrastingColor : undefined;
    var semanticColorBase = variant === 'bordered' ? borderedColor : 'base';
    if ((role === 'checkbox' || role === 'radio') && ariaChecked === undefined) {
      warn('Chips with checkbox or radio roles must have aria-checked="true/false" for accessibility.');
    }
    return /*#__PURE__*/React.createElement(ChipComponent, Object.assign({
      className: addColorSet(contrastingColorSet, className),
      as: as || defaultElement,
      role: role || _defaultRole,
      href: !disabled ? href : undefined,
      "aria-checked": ariaChecked,
      disabled: disabled,
      ref: handleAddingChipRef,
      "data-encore-id": ENCORE_DATA_IDS.Chip,
      "data-encore-chip-id": dataEncoreChipId,
      $variant: variant || 'tinted',
      $semanticColorBase: semanticColorBase,
      $size: _chipSize,
      $isUsingKeyboard: isUsingKeyboard,
      $useBrowserDefaultFocusStyle: useBrowserDefaultFocusStyle
    }, props), IconLeading && /*#__PURE__*/React.createElement(ChipIcon, {
      icon: IconLeading,
      size: _chipSize
    }), imageSrc && /*#__PURE__*/React.createElement(ChipImage, {
      src: imageSrc,
      size: _chipSize
    }), children && /*#__PURE__*/React.createElement(Label, {
      variant: _chipSize === 'medium' ? 'bodyMedium' : 'bodySmall',
      $chipSize: _chipSize
    }, children), IconTrailing && /*#__PURE__*/React.createElement(ChipIcon, {
      icon: IconTrailing,
      size: _chipSize
    }));
  }
  var defaultRole = href ? 'link' : 'checkbox';
  var chipSize = size ? getLegacyControlSize(size) : 'sm';
  var legacyChipClassName = function legacyChipClassName() {
    if (href) {
      return chipSize === 'sm' ? textClass('bodySmallBold') : textClass('bodyMediumBold');
    }
    return chipSize === 'sm' ? textClass('bodySmall') : textClass('bodyMedium');
  };
  return /*#__PURE__*/React.createElement(LegacyChipComponent, Object.assign({
    ref: ref,
    className: legacyChipClassName(),
    href: href,
    forwardedAs: as,
    role: role ? role : defaultRole,
    "aria-checked": href ? undefined : getAriaChecked(selected, role, ariaChecked),
    disabled: disabled,
    "data-encore-id": ENCORE_DATA_IDS.Chip,
    $secondary: secondary,
    $selected: selected,
    $size: chipSize,
    $isUsingKeyboard: isUsingKeyboard,
    $useBrowserDefaultFocusStyle: useBrowserDefaultFocusStyle
  }, props), /*#__PURE__*/React.createElement(LegacyChipInner, {
    selected: selected,
    isUsingKeyboard: isUsingKeyboard,
    size: chipSize,
    secondary: secondary,
    iconLeading: iconLeading,
    iconTrailing: iconTrailing,
    className: selected ? addColorSet(selectedColorSet, className) : className
  }, children));
});